import { useState, useMemo } from 'react';
import { formatDate } from '@/lib/format';
import { ScoreBadge } from '@/components/ui/score-badge';
import { TeamFitBadge } from '@/components/ui/team-fit-badge';
import { Button } from '@/components/ui/button';
import { MatchFilters } from '@/components/matches/match-filters';
import { MatchDetailDialog } from '@/components/matches/match-detail-dialog';
import { useMatches } from '@/hooks/use-matches';
import { useT } from '@/lib/i18n';

export function MatchesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('aiScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const t = useT();
  const { data, isLoading } = useMatches(page, 50);
  const allMatches = data?.data ?? [];

  // Client-side filter + sort
  const filtered = useMemo(() => {
    let result = allMatches;

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.profileName.toLowerCase().includes(lower) ||
          m.assignmentTitle.toLowerCase().includes(lower)
      );
    }

    if (minScore > 0) {
      result = result.filter((m) => {
        const score = m.aiScore
          ? parseFloat(m.aiScore)
          : m.heuristicScore
            ? parseFloat(m.heuristicScore)
            : 0;
        return score >= minScore;
      });
    }

    result = [...result].sort((a, b) => {
      const aVal = parseFloat((a as unknown as Record<string, string>)[sortField]) || 0;
      const bVal = parseFloat((b as unknown as Record<string, string>)[sortField]) || 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return result;
  }, [allMatches, search, minScore, sortField, sortDir]);

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th
      className="cursor-pointer px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground hover:text-foreground"
      onClick={() => toggleSort(field)}
    >
      {children} {sortField === field && (sortDir === 'desc' ? '↓' : '↑')}
    </th>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('matches.title')}</h1>

      <MatchFilters
        search={search}
        onSearchChange={setSearch}
        minScore={minScore}
        onMinScoreChange={setMinScore}
      />

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('matches.loading')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('matches.empty')}</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    Candidate
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    Assignment
                  </th>
                  <SortHeader field="heuristicScore">Heuristic</SortHeader>
                  <SortHeader field="aiScore">AI Score</SortHeader>
                  <SortHeader field="teamFitScore">Team Fit</SortHeader>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    Matched
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((match) => (
                  <tr
                    key={match.id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                    onClick={() => setSelectedMatchId(match.id)}
                  >
                    <td className="px-4 py-2.5 font-medium">{match.profileName}</td>
                    <td className="px-4 py-2.5">{match.assignmentTitle}</td>
                    <td className="px-4 py-2.5">
                      <ScoreBadge score={match.heuristicScore} />
                    </td>
                    <td className="px-4 py-2.5">
                      <ScoreBadge score={match.aiScore} />
                    </td>
                    <td className="px-4 py-2.5">
                      <TeamFitBadge score={match.teamFitScore} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">
                      {formatDate(match.matchedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Showing {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={allMatches.length < 50}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <MatchDetailDialog
        matchId={selectedMatchId}
        onClose={() => setSelectedMatchId(null)}
        profileName={filtered.find((m) => m.id === selectedMatchId)?.profileName}
        assignmentTitle={filtered.find((m) => m.id === selectedMatchId)?.assignmentTitle}
      />
    </div>
  );
}
